import type { Extensions } from "@tiptap/core";
import { createContext, type FC, type PropsWithChildren, type RefObject, useContext } from "react";

export type EmailDraftPlacement = "floating" | "inline";

export interface EmailDraftAttachment {
  id: string;
  name: string;
  size?: number | null;
  removable: boolean;
  pending: boolean;
}

/**
 * What the shared draft components read from their host. The host also owns the react-hook-form
 * `Form` (with `to`, `cc`, `subject`, and `bcc` when `showBcc`); these components never create one.
 */
export interface EmailDraftContextValue {
  title: string;
  placement: EmailDraftPlacement;
  isMinimized: boolean;
  handleToggleMinimized: () => void;
  handleClose: () => void;
  closeLabel?: string;
  cardRef: RefObject<HTMLDivElement | null>;
  editorContainerRef: RefObject<HTMLDivElement | null>;
  isEditable: boolean;
  isSending: boolean;
  recipientsOpen: boolean;
  setRecipientsOpen: (open: boolean) => void;
  recipientsReadOnly: boolean;
  /** Keeps To fixed while Cc and Bcc stay editable, for a host whose send always goes to set recipients. */
  toReadOnly?: boolean;
  showBcc: boolean;
  handleUpdateRecipientsTo: (value: string[]) => void;
  handleUpdateRecipientsCc: (value: string[]) => void;
  handleUpdateRecipientsBcc: (value: string[]) => void;
  handleUpdateSubject: (value: string) => void;
  handleUpdateContent: (value: { html: string; text: string }) => void;
  attachments: EmailDraftAttachment[];
  handleUploadAttachments: (files: File[]) => Promise<void>;
  handleDeleteAttachment: (id: string) => void | Promise<void>;
  handleDownloadAttachment: (id: string) => void | Promise<void>;
  handleSend: () => Promise<void>;
  sendBlockedReason?: string;
  editorExtensions: Extensions;
}

const EmailDraftContext = createContext<EmailDraftContextValue | null>(null);

export const EmailDraftProvider: FC<PropsWithChildren<{ value: EmailDraftContextValue }>> = ({ value, children }) => (
  <EmailDraftContext.Provider value={value}>{children}</EmailDraftContext.Provider>
);

export const useEmailDraft = () => {
  const context = useContext(EmailDraftContext);

  if (!context) {
    throw new Error("useEmailDraft must be used within an EmailDraftProvider");
  }

  return context;
};
